import { forwardRef } from "react";

import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { Button, type ButtonProps } from "src/components/Button";
import { cn } from "src/shadcn/lib/utils";

import { FormikButton } from "./Button";

export interface FormikActionBlockProps {
  /** Additional CSS class names for the wrapper. */
  className?: string;
  /** Props forwarded to the submit button. */
  submitButtonProps?: ButtonProps;
  /** Props forwarded to the cancel button. */
  cancelButtonProps?: ButtonProps;
  /**
   * Override the auto-computed submitting state (e.g. from React Query mutation).
   * Falls back to Formik's `isSubmitting` when not provided.
   */
  isSubmitting?: boolean;
  /** Alignment of the buttons. */
  position?: "left" | "right";
}

const FormikActionBlock = forwardRef<HTMLDivElement, FormikActionBlockProps>(
  (
    {
      className,
      submitButtonProps,
      cancelButtonProps,
      isSubmitting: isSubmittingProp,
      position = "left",
    },
    ref
  ) => {
    const { t } = useTranslation();
    const {
      handleReset,
      isSubmitting: isFormikSubmitting,
      dirty,
    } = useFormikContext();

    const isSubmitting = isSubmittingProp ?? isFormikSubmitting;

    const cancelButton = (
      <Button
        data-testid="cancel-button"
        disabled={isSubmitting}
        label={t("neetoui.actionBlock.cancel", "Cancel")}
        variant="ghost"
        onClick={handleReset}
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
        {...cancelButtonProps}
      />
    );

    const submitButton = (
      <FormikButton
        data-testid="save-changes-button"
        disabled={isSubmitting || !dirty}
        label={t("neetoui.actionBlock.saveChanges", "Save changes")}
        type="submit"
        {...submitButtonProps}
      />
    );

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-2",
          position === "right" && "justify-end",
          className
        )}
      >
        {position === "right" ? (
          <>
            {cancelButton}
            {submitButton}
          </>
        ) : (
          <>
            {submitButton}
            {cancelButton}
          </>
        )}
      </div>
    );
  }
);

FormikActionBlock.displayName = "FormikActionBlock";

export { FormikActionBlock };
